import { useCallback, useMemo, useRef, useState } from 'react';
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowDown, ArrowUp, ArrowUpDown, MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SortDir, SortKey, UnitListRow } from '$lib/api/types';
import { Sparkline } from '$lib/components/charts/Sparkline';
import { Badge } from '$lib/components/ui/Badge';
import { RowMenu, type RowMenuAnchor } from '$lib/components/table/RowMenu';
import { cn } from '$lib/lib/cn';
import { formatDate, formatKr, formatKrPerSqm, formatRooms, formatSqm, formatYear } from '$lib/lib/formatters';

const ROW_HEIGHT = 32; // Decision 1.

export interface UnitTableProps {
  rows: UnitListRow[];
  /** Loading state while a fetch is in flight. */
  loading?: boolean;
  sort?: SortKey;
  dir?: SortDir;
  onSortChange: (sort: SortKey | undefined, dir: SortDir | undefined) => void;
  onRowOpen: (row: UnitListRow) => void;
  onRowOpenInNewTab: (row: UnitListRow) => void;
}

const STATUS_TONE: Record<UnitListRow['status'], 'positive' | 'warning' | 'neutral' | 'danger'> = {
  active: 'positive',
  pending: 'warning',
  rented: 'neutral',
  delisted: 'danger'
};

export function UnitTable({
  rows,
  loading,
  sort,
  dir,
  onSortChange,
  onRowOpen,
  onRowOpenInNewTab
}: UnitTableProps) {
  const { t } = useTranslation();
  const sorting: SortingState = useMemo(
    () => (sort ? [{ id: sort, desc: dir === 'desc' }] : []),
    [sort, dir]
  );

  const [menu, setMenu] = useState<RowMenuAnchor | null>(null);
  const closeMenu = useCallback(() => setMenu(null), []);

  const columns = useMemo<ColumnDef<UnitListRow>[]>(
    () => [
      {
        id: 'address',
        accessorKey: 'address',
        header: t('table.columnAddress'),
        size: 320,
        cell: (ctx) => (
          <span className="block truncate" title={ctx.getValue<string>()}>
            {ctx.getValue<string>()}
          </span>
        )
      },
      {
        id: 'city',
        accessorKey: 'city',
        // Built-in TanStack sort handler is bypassed: this header has TWO
        // independently clickable halves (Zip + City) that drive different
        // sort keys via onSortChange directly. enableSorting=false stops
        // TanStack from showing its own arrow next to the column.
        enableSorting: false,
        header: () => (
          <span className="inline-flex items-center gap-1">
            <button
              type="button"
              data-testid="zip-city-zip-btn"
              onClick={(e) => {
                e.stopPropagation();
                const next: SortDir =
                  sort === 'postnummer' && dir === 'asc' ? 'desc' : 'asc';
                onSortChange('postnummer', next);
              }}
              className={cn(
                'inline-flex items-center gap-0.5 hover:text-fg',
                sort === 'postnummer' && 'text-fg'
              )}
              aria-label={t('table.columnZipAriaLabel', { defaultValue: 'Sort by zip code' })}
            >
              {t('table.columnZip', { defaultValue: 'Zip' })}
              {sort === 'postnummer' &&
                (dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
            </button>
            <span aria-hidden className="text-fg-subtle">/</span>
            <button
              type="button"
              data-testid="zip-city-city-btn"
              onClick={(e) => {
                e.stopPropagation();
                const next: SortDir =
                  sort === 'city' && dir === 'asc' ? 'desc' : 'asc';
                onSortChange('city', next);
              }}
              className={cn(
                'inline-flex items-center gap-0.5 hover:text-fg',
                sort === 'city' && 'text-fg'
              )}
              aria-label={t('table.columnCityAriaLabel', { defaultValue: 'Sort by city' })}
            >
              {t('table.columnCity')}
              {sort === 'city' &&
                (dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
            </button>
          </span>
        ),
        size: 140,
        cell: (ctx) => (
          <span className="block truncate text-fg-muted">
            <span className="num text-fg-subtle">{ctx.row.original.postnummer}</span>{' '}
            {ctx.getValue<string>()}
          </span>
        )
      },
      {
        id: 'm2',
        accessorKey: 'm2',
        header: t('table.columnM2'),
        size: 80,
        meta: { numeric: true },
        cell: (ctx) => <span className="num block w-full truncate">{formatSqm(ctx.getValue<number | null>())}</span>
      },
      {
        id: 'rooms',
        accessorKey: 'rooms',
        header: t('table.columnRooms'),
        size: 70,
        meta: { numeric: true },
        cell: (ctx) => <span className="num block w-full truncate">{formatRooms(ctx.getValue<number | null>())}</span>
      },
      {
        id: 'yearBuilt',
        accessorKey: 'yearBuilt',
        header: t('table.columnYear'),
        size: 70,
        meta: { numeric: true },
        cell: (ctx) => <span className="num block w-full truncate">{formatYear(ctx.getValue<number | null>())}</span>
      },
      {
        id: 'rentKrPerMonth',
        accessorKey: 'rentKrPerMonth',
        header: t('table.columnRent'),
        size: 110,
        meta: { numeric: true },
        cell: (ctx) => <span className="num block w-full truncate">{formatKr(ctx.getValue<number | null>())}</span>
      },
      {
        id: 'krPerSqm',
        accessorKey: 'krPerSqm',
        header: t('table.columnKrPerSqm'),
        size: 110,
        meta: { numeric: true },
        cell: (ctx) => <span className="num block w-full truncate">{formatKrPerSqm(ctx.getValue<number | null>())}</span>
      },
      {
        id: 'spark',
        header: t('table.column12mo'),
        size: 100,
        enableSorting: false,
        // Sparkline cell opts out of overflow clipping so the hover tooltip can
        // float above the row without being cut off by the cell's bounds.
        meta: { numeric: true, allowOverflow: true },
        cell: (ctx) => <Sparkline data={ctx.row.original.spark} />
      },
      {
        id: 'lastSeen',
        accessorKey: 'lastSeen',
        header: t('table.columnLastSeen'),
        size: 100,
        cell: (ctx) => <span className="block w-full truncate text-fg-muted">{formatDate(ctx.getValue<string | null>())}</span>
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('table.columnStatus'),
        size: 88,
        enableSorting: false,
        cell: (ctx) => (
          <Badge tone={STATUS_TONE[ctx.row.original.status]}>{t(`status.${ctx.row.original.status}`)}</Badge>
        )
      },
      {
        id: 'actions',
        header: '',
        size: 36,
        enableSorting: false,
        cell: (ctx) => (
          <button
            type="button"
            aria-label={t('table.rowActionsAriaLabel')}
            data-testid="row-kebab"
            className="grid h-6 w-6 place-items-center rounded-xs text-fg-subtle hover:bg-surface-3 hover:text-fg"
            onClick={(e) => {
              e.stopPropagation();
              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
              setMenu({ x: rect.right, y: rect.bottom, rect, row: ctx.row.original });
            }}
          >
            <MoreHorizontal size={14} />
          </button>
        )
      }
    ],
    // sort + dir + onSortChange are read inside the city column's custom
    // header (Zip / City split — TanStack's built-in sort is bypassed
    // there). Without these in the deps, the active-sort arrow next to
    // the right half wouldn't update on toggle.
    [t, sort, dir, onSortChange]
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    manualSorting: true,
    onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      const first = next[0];
      if (!first) onSortChange(undefined, undefined);
      else onSortChange(first.id as SortKey, first.desc ? 'desc' : 'asc');
    },
    getCoreRowModel: getCoreRowModel()
  });

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const virt = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12
  });

  const totalSize = virt.getTotalSize();
  const items = virt.getVirtualItems();
  // Sum of declared column widths is the table's natural width. We hand it to
  // the table via min-width so a narrow viewport horizontally scrolls instead
  // of squashing the columns.
  const totalColumnsWidth = table.getAllLeafColumns().reduce((acc, col) => acc + col.getSize(), 0);
  const leafColumnCount = table.getAllLeafColumns().length;
  // Top/bottom spacer-row heights translate the virtualizer's window into the
  // native table layout: the leading spacer pushes the first visible row down
  // to its scroll offset, and the trailing spacer keeps the table tall enough
  // for the scroll thumb to reflect the full dataset.
  const paddingTop = items.length > 0 ? items[0].start : 0;
  const paddingBottom = items.length > 0 ? totalSize - items[items.length - 1].end : 0;

  return (
    <div
      ref={scrollRef}
      data-testid="unit-table-scroll"
      className="relative flex-1 overflow-auto"
      role="region"
      aria-label={t('table.ariaLabel')}
      onScroll={() => menu && closeMenu()}
    >
      <table
        className="table-fixed border-collapse text-sm"
        style={{ width: totalColumnsWidth, minWidth: totalColumnsWidth }}
      >
        <colgroup>
          {table.getAllLeafColumns().map((col) => (
            <col key={col.id} style={{ width: col.getSize() }} />
          ))}
        </colgroup>
        <thead className="sticky top-0 z-10 bg-surface-2 text-2xs uppercase tracking-wider text-fg-subtle shadow-elev-1">
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id} className="border-b border-rule">
              {group.headers.map((header) => {
                const numeric = (header.column.columnDef.meta as { numeric?: boolean } | undefined)?.numeric;
                const sortDir = header.column.getIsSorted();
                const canSort = header.column.getCanSort();
                return (
                  <th
                    key={header.id}
                    className={cn(
                      'px-3 py-2 font-medium',
                      numeric ? 'text-right' : 'text-left',
                      canSort && 'cursor-pointer select-none hover:text-fg'
                    )}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    aria-sort={sortDir === 'asc' ? 'ascending' : sortDir === 'desc' ? 'descending' : 'none'}
                  >
                    <span className={cn('inline-flex items-center gap-1', numeric && 'flex-row-reverse')}>
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {canSort && (
                        sortDir === 'asc' ? <ArrowUp size={12} /> :
                        sortDir === 'desc' ? <ArrowDown size={12} /> :
                        <ArrowUpDown size={12} className="opacity-40" />
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {paddingTop > 0 && (
            <tr aria-hidden="true">
              <td colSpan={leafColumnCount} style={{ height: paddingTop }} className="p-0" />
            </tr>
          )}
          {items.map((vi) => {
            const row = table.getRowModel().rows[vi.index];
            const original = row.original;
            return (
              <tr
                key={row.id}
                data-testid="unit-row"
                data-index={vi.index}
                onClick={() => onRowOpen(original)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setMenu({ x: e.clientX, y: e.clientY, row: original });
                }}
                className={cn(
                  'cursor-pointer text-fg row-line hover:bg-surface-2',
                  vi.index % 2 === 1 && 'bg-surface-2/40',
                  menu?.row.id === original.id && 'bg-accent/8'
                )}
                style={{ height: ROW_HEIGHT }}
              >
                {row.getVisibleCells().map((cell) => {
                  const meta = cell.column.columnDef.meta as
                    | { numeric?: boolean; allowOverflow?: boolean }
                    | undefined;
                  const numeric = meta?.numeric;
                  const allowOverflow = meta?.allowOverflow;
                  return (
                    <td
                      key={cell.id}
                      className={cn(
                        'px-3 align-middle',
                        numeric ? 'text-right' : 'text-left',
                        // Clip overflowing content (long numbers, oversized SVG)
                        // so it cannot bleed across into adjacent columns. The
                        // sparkline cell opts out via allowOverflow so its
                        // hover tooltip can float above the row.
                        !allowOverflow && 'overflow-hidden'
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            );
          })}
          {paddingBottom > 0 && (
            <tr aria-hidden="true">
              <td colSpan={leafColumnCount} style={{ height: paddingBottom }} className="p-0" />
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={leafColumnCount} className="px-6 py-12 text-center text-fg-muted">
                {t('table.emptyAfterFilter')}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <RowMenu
        anchor={menu}
        onClose={closeMenu}
        onOpen={(row) => {
          closeMenu();
          onRowOpen(row);
        }}
        onOpenInNewTab={(row) => {
          closeMenu();
          onRowOpenInNewTab(row);
        }}
      />
    </div>
  );
}
